import {UI} from "../../../stem-core/ui/UIBase.js";
import {DashboardTitle} from "../../common/DashboardTitle.js";
import {CSVFileFormat, CSVFileInput} from "../../common/input/CSVFileInput.jsx";
import {Button} from "../../../stem-core/ui/button/Button.jsx";
import {State} from "../../../stem-core/state/State.js";
import {apiGetActiveSubscriberEmails, Subscription} from "../../../client/state/SubscriptionStore.js";
import {normalizeEmail} from "../../../client/state/SocialAccountStore.js";
import {TaskStatus} from "../../../client/state/misc/GenericEnums.js";
import {apiMerchantSyncExternalUser} from "../../../client/state/merchant/MerchantUserStore.js";
import {BlinkInputField} from "../../common/Input.jsx";
import {DateInput} from "../../ui/input/DateInput.jsx";
import {StemDate} from "../../../stem-core/time/Date.js";
import {TimeUnit} from "../../../stem-core/time/Duration.js";
import {ArrayPaginator} from "../../../stem-core/state/pagination/EndpointPaginator";
import {SimpleTable} from "../../ui/SimpleTable.jsx";
import {DashboardSectionTitle} from "../../common/DashboardSection.jsx";
import {BaseTaskProgressStatus} from "./BaseTaskProgressStatus.jsx";
import {TaskProgressStatusDescriptor} from "./TaskProgressStatusDescriptor.jsx";
import {CheckboxInput} from "../../../stem-core/ui/input/checkbox/CheckboxInput.jsx";
import {CSVBuilder} from "../../../stem-core/base/CSV.js";
import {NOOP_FUNCTION, slugify} from "../../../stem-core/base/Utils.js";
import {ExternalAppType} from "../../../client/state/ExternalAppConfigStore.js";
import {BaseStore} from "../../../stem-core/state/Store.js";
import {RadioButtonsInput} from "../../../core/ui/input/radio/RadioButtonsInput.jsx";


const SyncMode = {
    FULL: "Full sync",
    FIX_MISSING: "Only fix missing subscribers",
};

// Emails are POSTed in the body, so batches can be large — fewer round-trips for big CDS exports.
const EMAIL_LOOKUP_CHUNK_SIZE = 1000;

function chunkArray(array, size) {
    const chunks = [];
    for (let index = 0; index < array.length; index += size) {
        chunks.push(array.slice(index, index + size));
    }
    return chunks;
}

// API failures reach us either as {message} or nested as {error: {message}} (a rejected xhr keeps the
// raw body); pull whichever is present so the UI shows the real message instead of "[object Object]".
function apiErrorMessage(error) {
    return error?.message || error?.error?.message || String(error);
}


// Finds which CSV emails already hold a currently-active Blink subscription by querying only those
// emails (batched) instead of scanning every subscriber, and exposes the CSV rows still missing one.
class MissingSubscriberFinder extends UI.Element {
    status = new BaseTaskProgressStatus();
    missingEntries = [];

    async find() {
        if (this.status.isInProgress()) {
            return;
        }
        const {csvEntries} = this.options;

        this.status = new BaseTaskProgressStatus(csvEntries.length);
        this.missingEntries = [];
        const {status} = this;
        this.attachChangeListener(status, () => this.dispatchChange());

        const activeEmails = new Set();
        try {
            status.setProgress(0);

            for (const chunk of chunkArray(csvEntries, EMAIL_LOOKUP_CHUNK_SIZE)) {
                const emails = chunk.map(entry => normalizeEmail(entry.email));
                const active = await apiGetActiveSubscriberEmails(emails);
                for (const email of active) {
                    activeEmails.add(normalizeEmail(email));
                }
                status.incremProgress(chunk.length);
                if (status.shouldStop) {
                    break;
                }
            }

            this.missingEntries = csvEntries.filter(entry => !activeEmails.has(normalizeEmail(entry.email)));
            status.setDone();
        } catch (error) {
            status.setErrorMessage(apiErrorMessage(error));
        }
    }

    render() {
        const {status} = this;
        return [
            <div>
                <Button disabled={status.isInProgress()} label="Find missing subscribers" onClick={() => this.find()} />
                {status.isInProgress() && <Button label="Stop" onClick={() => status.setStop()} />}
            </div>,
            status.phase !== TaskStatus.PENDING && <TaskProgressStatusDescriptor status={status} />
        ];
    }
}

class ExternalUserSyncController extends UI.Element {
    errors = [];
    warnings = [];

    // The CSV says the user should hold an active subscription through endDate. After syncing (which re-fetches the
    // live membership from CDS Core), confirm that's what we ended up with; show a warning otherwise.
    validateSyncedSubscription(response, entry) {
        const {endDate} = entry;

        const state = new State();
        const SubscriptionStore = BaseStore("Subscription", {state}, Subscription);
        state.importState(response.state || {});

        const activeSubs = SubscriptionStore.all().filter(sub => sub.isActive());
        if (activeSubs.length === 0) {
            return "synced but has no active subscription";
        }
        if (endDate && !activeSubs.some(sub => sub.getActiveUntil() >= endDate)) {
            const latest = activeSubs.map(sub => sub.getActiveUntil()).sort((a, b) => b - a)[0];
            return `active subscription ends ${latest.format("YYYY-MM-DD")}, before expected ${endDate.format("YYYY-MM-DD")}`;
        }
        return null;
    }

    async processEntries() {
        const {entries} = this.options;

        this.status = new BaseTaskProgressStatus(entries.length);
        this.errors = [];
        this.warnings = [];

        for (const entry of entries) {
            const {email} = entry;

            try {
                const response = await apiMerchantSyncExternalUser({
                    provider: ExternalAppType.CDS,
                    appId: "RU",
                    externalUserId: email,
                    email,
                });
                const warningMessage = this.validateSyncedSubscription(response, entry);
                if (warningMessage) {
                    this.warnings.push({email, warningMessage});
                }
            } catch (error) {
                const errorMessage = apiErrorMessage(error);
                if (this.stopOnErrorInput.getValue()) {
                    this.status.setStop(true);
                }
                this.errors.push({email, errorMessage});
            } finally {
                this.status.incremProgress();
            }

            if (this.status.shouldStop) {
                break;
            }
            this.redraw();
        }
        this.status.setDone();
        this.redraw();
    }

    render() {
        const {name} = this.options;
        return [
            <div style={{display: "flex"}}>
                <CheckboxInput ref="stopOnErrorInput" label="Stop on error" initialValue={true} style={{alignSelf: "center"}} />
                <Button disabled={this.isLoading} label={"Sync " + name} onClick={() => this.processEntries()}/>
                {this.status?.phase === TaskStatus.IN_PROGRESS && <Button label="Stop sync" onClick={() => this.status.setStop()} />}
            </div>,

            this.status && <TaskProgressStatusDescriptor status={this.status}/>,

            this.errors.length > 0 && [
                <div>{this.errors.length} error encountered:</div>,
                this.errors.map(error => <li>{error.email}: {error.errorMessage}</li>),
            ],

            this.warnings.length > 0 && [
                <div>{this.warnings.length} subscription mismatch(es):</div>,
                this.warnings.map(warning => <li>{warning.email}: {warning.warningMessage}</li>),
            ]

        ];
    }
}


class ExternalUserSyncSetManager extends UI.Element {
    exportCSV = NOOP_FUNCTION;

    render() {
        const {workingSet} = this.options;
        const {entries} = workingSet;
        const name = String(workingSet);
        const firstEntry = entries[0];
        if (!firstEntry) {
            return <div>No entries in the chosen set</div>;
        }

        const paginator = new ArrayPaginator(entries, 5);
        const columns = Object.keys(firstEntry).map(key => [key, (obj) => obj[key]]);

        // A bit ugly, meh
        this.exportCSV = () => CSVBuilder.saveFile(columns, entries, `Sync-${slugify(name)}-${new StemDate().format("YYYY-MM-DD")}.csv`);

        return [
            <div>
                <Button label="Export to csv..." onClick={() => this.exportCSV()}/>
            </div>,
            <SimpleTable key={Math.random()} columns={columns} paginator={paginator} />,
            <ExternalUserSyncController entries={entries} name={name} />
        ];
    }
}


export class CDSSyncSubscribersPage extends UI.Element {
    defaultStartsAfter = StemDate.now().subtract(TimeUnit.WEEK); // Ignore very recently-started rows by default.

    getSyncMode() {
        return this.syncModeInput?.getValue() || SyncMode.FULL;
    }

    // Rows from the CSV, scoped by the "starts after" cutoff (a pure client-side filter on the CSV start date).
    getFilteredCsvEntries() {
        const entries = this.csvInput?.getEntriesAsObjects() || [];
        const cutoff = this.startsAfterInput?.getValue();
        if (!cutoff) {
            return entries;
        }
        return entries.filter(entry => !entry.startDate || entry.startDate <= cutoff);
    }

    renderMissingSyncActions() {
        const {finder} = this;
        if (!finder || finder.status.phase !== TaskStatus.SUCCEEDED) {
            return null;
        }

        const {missingEntries} = finder;
        const lookedUp = finder.options.csvEntries.length;
        const workingSet = {toString: () => "CSV subscribers without an active subscription", entries: missingEntries};

        return [
            <div style={{paddingTop: 4}}>{missingEntries.length} of {lookedUp} CSV subscribers have no active subscription</div>,
            <DashboardSectionTitle title="Run sync" />,
            <ExternalUserSyncSetManager workingSet={workingSet} />,
        ];
    }

    renderSyncSection() {
        const csvEntries = this.getFilteredCsvEntries();
        if (csvEntries.length === 0) {
            return null;
        }

        if (this.getSyncMode() === SyncMode.FULL) {
            const workingSet = {toString: () => "All CSV subscribers", entries: csvEntries};
            return [
                <div style={{paddingTop: 4}}>{csvEntries.length} subscribers in CSV to sync</div>,
                <DashboardSectionTitle title="Run sync" />,
                <ExternalUserSyncSetManager workingSet={workingSet} />,
            ];
        }

        return [
            <DashboardSectionTitle title="Find missing subscribers" />,
            <MissingSubscriberFinder ref="finder" csvEntries={csvEntries} onChange={() => this.redraw()} />,
            this.renderMissingSyncActions(),
        ];
    }

    render() {
        return [
            <DashboardTitle title="Sync CDS Subscribers" description="Open the CDS account file and find account subsets to update" />,

            <BlinkInputField label="Sync mode">
                <RadioButtonsInput ref="syncModeInput" values={[SyncMode.FULL, SyncMode.FIX_MISSING]}
                                   initialValue={SyncMode.FULL} onChange={() => this.redraw()} />
            </BlinkInputField>,

            <DashboardSectionTitle title="Open CSV File" />,
            <CSVFileInput ref="csvInput" fileFormat={CSVFileFormat.CDS_SUBSCRIBER} onChange={() => this.redraw()} />,

            <BlinkInputField label="Ignore if subscription starts after">
                <DateInput ref="startsAfterInput" initialValue={this.defaultStartsAfter} onChange={() => this.redraw()} />
            </BlinkInputField>,

            this.renderSyncSection(),
        ];
    }
}
